import { fixedUi } from '@/i18n/fixedUi';
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import RequiredMark from '@/tiktok-platform/components/RequiredMark';
import TiktokSwitch from '@/tiktok-platform/components/TiktokSwitch';
import {
  buildSizeChartTemplatePayload,
  validateSizeChartForm,
} from '@/tiktok-platform/utils/tiktokSizeChart';
import {
  buildOfficialSizeChartTemplatePayload,
  saveOfficialSizeChartAsReusableTemplate,
  validateOfficialSizeChartDraft,
} from '@/tiktok-platform/utils/tiktokOfficialSizeChart';
import { nextTemplatePriority } from '@/tiktok-platform/utils/tiktokTemplatePriority';
import { tiktokPlatformAPI } from '@/utils/api';
import { adminAPI } from '@/utils/adminApi';
import { showToast } from '@/utils/toast';
import { chartText } from '@/i18n/chartText';
import { useTranslation } from 'react-i18next';

/**
 * 另存尺码库：元信息弹窗（字段/样式对齐属性库保存弹窗）。
 * - 官方尺码表路径：传入 officialSizeChart + sizeClass 等
 * - 旧矩阵路径：传入 sizeChart / chartRows / measureAttrs
 */
export default function SaveSizeChartTemplateMetaModal({
  open,
  shopId,
  officialMode = false,
  shopType = '',
  categoryId = '',
  productTypeId = '',
  categoryPathLabel = '',
  sizeSaleAttributeId = '',
  sizeChart = {},
  chartRows = [],
  measureAttrs = [],
  sizeAttr = null,
  selectedSizeIds = [],
  selectedSizeLabels = [],
  skcList = [],
  /** 官方尺码表草稿（TiktokOfficialSizeChartPanel） */
  officialSizeChart = null,
  sizeClass = null,
  /** 打开时预填款号（如发品页已有款号） */
  defaultStyleNo = '',
  /**
   * 发品页「保存为模板」：只写站内尺码库，不同步平台（默认 true）。
   * 尺码库本页新建仍走 Create Size Chart 推平台。
   */
  localOnly = true,
  onClose,
  onSaved,
}) {
  useTranslation('workbench');
  const [name, setName] = useState('');
  const [styleNo, setStyleNo] = useState('');
  const [remark, setRemark] = useState('');
  const [autoRuleEnabled, setAutoRuleEnabled] = useState(false);
  const [sortOrder, setSortOrder] = useState(0);
  const [saving, setSaving] = useState(false);
  const [priorityLoading, setPriorityLoading] = useState(false);

  const isOfficialChart = Boolean(
    officialSizeChart
    && typeof officialSizeChart === 'object'
    && (Array.isArray(officialSizeChart.records) || officialSizeChart.mode),
  );

  useEffect(() => {
    if (!open) return;
    const labels = (Array.isArray(selectedSizeLabels) ? selectedSizeLabels : [])
      .map((n) => String(n || '').trim())
      .filter(Boolean);
    setName(labels.length ? `尺码表-${labels.join('/')}` : '');
    setStyleNo(String(defaultStyleNo || '').trim());
    setRemark('');
    setAutoRuleEnabled(false);
    setSortOrder(0);
    setSaving(false);
    // 仅在弹窗打开时预填，避免输入过程中被重置
    // eslint-disable-next-line react-hooks/exhaustive-deps -- open 边沿初始化
  }, [open]);

  useEffect(() => {
    if (!open || !productTypeId || (!officialMode && !shopId)) return undefined;
    let cancelled = false;
    setPriorityLoading(true);
    const load = officialMode
      ? adminAPI.listOfficialTiktokSizeChartTemplates({
        productTypeId,
        shopType,
        page: 1,
        pageSize: 100,
      })
      : tiktokPlatformAPI.listSizeChartTemplates(shopId, {
        productTypeId,
        page: 1,
        pageSize: 100,
      });
    load
      .then((res) => {
        if (cancelled) return;
        setSortOrder(nextTemplatePriority(res?.items));
      })
      .catch(() => {
        if (!cancelled) setSortOrder(0);
      })
      .finally(() => {
        if (!cancelled) setPriorityLoading(false);
      });
    return () => { cancelled = true; };
  }, [open, shopId, productTypeId, officialMode, shopType]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape' && !saving) onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, saving, onClose]);

  if (!open) return null;

  const submit = async () => {
    if (!officialMode && !shopId) {
      showToast(chartText('returnList.pickShop', fixedUi("请先选择店铺")), 'error');
      return;
    }
    const styleNoValue = String(styleNo || '').trim();
    if (!styleNoValue) {
      showToast(chartText('saveTpl.needStyle', fixedUi("请填写款号")), 'error');
      return;
    }
    const title = String(name || '').trim();
    if (!title) {
      showToast(chartText('saveTpl.needName', fixedUi("请填写模板名称")), 'error');
      return;
    }
    if (!categoryId || !productTypeId) {
      showToast(chartText('saveTpl.needCategory', fixedUi("缺少适用类目")), 'error');
      return;
    }

    setSaving(true);
    try {
      let payload;
      if (isOfficialChart) {
        let draft = {
          mode: officialSizeChart.mode || 'create',
          sizeClass: sizeClass || officialSizeChart.sizeClass || null,
          selectedBusinessIds: Array.isArray(officialSizeChart.selectedBusinessIds)
            ? officialSizeChart.selectedBusinessIds
            : [],
          selectedLibraryId: String(officialSizeChart.selectedLibraryId || '').trim(),
          records: Array.isArray(officialSizeChart.records) ? officialSizeChart.records : [],
          groupList: Array.isArray(officialSizeChart.groupList) ? officialSizeChart.groupList : [],
          elementList: Array.isArray(officialSizeChart.elementList) ? officialSizeChart.elementList : [],
          measurementType: String(officialSizeChart.measurementType || '').trim(),
        };
        const sizeCheck = validateOfficialSizeChartDraft({
          required: true,
          mode: 'create',
          sizeClass: draft.sizeClass,
          records: draft.records,
          groupList: draft.groupList,
          elementList: draft.elementList,
        });
        if (!sizeCheck.ok) {
          showToast(sizeCheck.message || chartText('publishExtra.fillComplete', fixedUi('请先完整填写尺寸信息')), 'error');
          return;
        }

        let platformBusinessIds = draft.selectedBusinessIds
          .map(Number)
          .filter((id) => id > 0);

        // 非 localOnly：先尝试写平台可复用模板；失败时后端已回退本地，勿再 create 第二条
        if (
          !localOnly
          && shopId
          && !platformBusinessIds.length
          && draft.mode === 'create'
        ) {
          try {
            const saved = await saveOfficialSizeChartAsReusableTemplate({
              shopId,
              catId: categoryId,
              sizeClass: draft.sizeClass,
              chartName: title,
              records: draft.records,
              groupList: draft.groupList,
              elementList: draft.elementList,
              measurementType: draft.measurementType,
              api: tiktokPlatformAPI,
            });
            if (saved?.platformWriteSkipped) {
              showToast(
                saved.message || chartText('saveTpl.platformSkip', fixedUi('平台尺码表写入不可用，已保存到本地尺码库')),
                'warning',
              );
              onSaved?.({ name: title, styleNo: styleNoValue, item: saved.item || null });
              onClose?.();
              return;
            }
            if (saved?.businessId) {
              platformBusinessIds = [Number(saved.businessId)];
              draft = {
                ...draft,
                mode: 'template',
                selectedBusinessIds: platformBusinessIds,
                selectedLibraryId: String(saved.businessId),
              };
            }
          } catch (syncErr) {
            showToast(
              syncErr?.message || chartText('saveTpl.syncFail', fixedUi('同步平台尺码表库失败，将仅保存到站内尺码库')),
              'warning',
            );
          }
        }

        payload = buildOfficialSizeChartTemplatePayload({
          categoryId,
          productTypeId,
          categoryPathLabel,
          selectedSizeLabels,
          selectedSizeIds,
          sizeClass: draft.sizeClass,
          officialSizeChart: draft,
          platformBusinessIds,
        });
      } else {
        payload = buildSizeChartTemplatePayload({
          sizeChart,
          chartRows,
          measureAttrs,
          sizeSaleAttributeId,
          productTypeId,
          categoryId,
          categoryPathLabel,
        });
        if (!payload.sizeChart || !Object.keys(payload.sizeChart).length) {
          showToast(chartText('saveTpl.emptyChart', fixedUi('当前尺码表为空，无法保存')), 'error');
          return;
        }
        const sizeCheck = validateSizeChartForm({
          sizeChart,
          measureAttrs,
          sizeAttr,
          selectedSizeIds,
          skcList,
        });
        if (!sizeCheck.ok) {
          showToast(sizeCheck.message || chartText('saveTpl.chartInvalid', fixedUi('尺码表不符合平台填写要求')), 'error');
          return;
        }
      }

      const body = {
        name: title,
        styleNo: styleNoValue,
        remark: String(remark || '').trim() || undefined,
        productTypeId,
        categoryId,
        categoryPathLabel: categoryPathLabel || undefined,
        sizeSaleAttributeId: sizeSaleAttributeId || undefined,
        autoRuleEnabled,
        sortOrder: Number.isFinite(Number(sortOrder)) ? Number(sortOrder) : 0,
        payload,
      };
      if (officialMode) {
        body.shopType = shopType;
        await adminAPI.createOfficialTiktokSizeChartTemplate(body);
      } else {
        await tiktokPlatformAPI.createSizeChartTemplate(shopId, body);
      }
      showToast(
        officialMode
          ? chartText('saveTpl.savedOfficialSize', fixedUi('已保存到官方尺码库'))
          : (localOnly ? chartText('saveTpl.savedLocalOnly', fixedUi('已保存到站内尺码库（未提交平台）')) : chartText('saveTpl.savedSize', fixedUi('已保存到尺码库'))),
        'success',
      );
      onSaved?.({ name: title, styleNo: styleNoValue, payload });
      onClose?.();
    } catch (e) {
      showToast(e?.message || chartText('saveTpl.saveSizeFail', fixedUi('保存到尺码库失败')), 'error');
    } finally {
      setSaving(false);
    }
  };

  return createPortal(
    <div className="tko-batch-modal tko-attr-tpl-modal" role="presentation">
      <button
        type="button"
        className="tko-batch-modal__backdrop"
        aria-label={chartText('moduleShell.close', fixedUi("关闭"))}
        onClick={() => { if (!saving) onClose?.(); }}
      />
      <div
        className="tko-batch-modal__panel tko-attr-tpl-modal__panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="tko-size-chart-save-meta-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="tko-batch-modal__head">
          <h2 id="tko-size-chart-save-meta-title">{officialMode ? chartText('saveTpl.saveOfficialSize', fixedUi("保存到官方尺码库")) : chartText('saveTpl.saveAsSize', fixedUi("保存为尺码模板"))}</h2>
          <button type="button" className="tko-batch-modal__close" aria-label={chartText('moduleShell.close', fixedUi("关闭"))} disabled={saving} onClick={onClose}>×</button>
        </div>
        <div className="tko-batch-modal__body">
          <div className="tko-wizard__form tko-template-create-form">
            <p className="tko-wizard__inline-hint">
              {localOnly
                ? chartText('saveTpl.hintLocal', fixedUi('将当前填写的尺寸保存到站内尺码库（含款号与自动规则），下次可从下拉选用。发品页保存默认仅本地，不会提交到 TikTok 后台。'))
                : chartText('saveTpl.hintSync', fixedUi('将当前填写的尺寸保存到站内尺码库（含款号与自动规则），下次可从下拉选用；同时会同步到平台尺码表库。'))}
            </p>
            <label className="tko-field tko-field--full">
              <span className="tko-field__label">{chartText('productTemplate.colStyleNo', fixedUi("款号"))} <RequiredMark /></span>
              <input
                className="tko-input"
                value={styleNo}
                disabled={saving}
                maxLength={128}
                placeholder={chartText('saveTpl.stylePh', fixedUi("请输入款号"))}
                onChange={(e) => setStyleNo(e.target.value)}
              />
            </label>
            <label className="tko-field tko-field--full">
              <span className="tko-field__label">{chartText('productTemplate.colName', fixedUi("模板名称"))} <RequiredMark /></span>
              <input
                className="tko-input"
                value={name}
                disabled={saving}
                maxLength={128}
                placeholder={chartText('saveTpl.namePhSize', fixedUi('如：女装基础尺码表'))}
                onChange={(e) => setName(e.target.value)}
              />
            </label>
            <label className="tko-field tko-field--full">
              <span className="tko-field__label">{chartText('productTemplate.colRemark', fixedUi("备注"))}</span>
              <input
                className="tko-input"
                value={remark}
                disabled={saving}
                maxLength={200}
                placeholder={chartText('publishSpec.patternOptional', fixedUi("可选"))}
                onChange={(e) => setRemark(e.target.value)}
              />
            </label>
            <div className="tko-field tko-field--full tko-template-auto-rule">
              <div className="tko-template-auto-rule__switch">
                <TiktokSwitch
                  checked={autoRuleEnabled}
                  disabled={saving}
                  ariaLabel={chartText('productTemplate.colAutoRule', fixedUi('自动规则'))}
                  onChange={setAutoRuleEnabled}
                />
                <span>{chartText('productTemplate.colAutoRule', fixedUi("自动规则"))}</span>
              </div>
              <p className="tko-wizard__inline-hint">
                {chartText('saveTpl.autoHint', fixedUi('开启后，发品选中该类目时自动带出。优先级仅在同类目内比较，数字越小越优先。'))}
              </p>
              <label className="tko-field">
                <span className="tko-field__label">{chartText('productTemplate.colPriority', fixedUi("优先级"))}</span>
                <input
                  className="tko-input"
                  type="number"
                  step={1}
                  value={sortOrder}
                  disabled={saving || priorityLoading}
                  onChange={(e) => {
                    const next = Number(e.target.value);
                    setSortOrder(Number.isFinite(next) ? next : 0);
                  }}
                />
              </label>
            </div>
            <label className="tko-field tko-field--full">
              <span className="tko-field__label">{chartText('photoLib.categoryApply', fixedUi("适用类目"))}</span>
              <input
                className="tko-input"
                value={categoryPathLabel || categoryId || '—'}
                disabled
                readOnly
              />
            </label>
          </div>
        </div>
        <div className="tko-batch-modal__foot">
          <button type="button" className="tko-btn tko-btn--ghost tko-btn--sm" disabled={saving} onClick={onClose}>
            {chartText('shop.cancel', fixedUi('取消'))}
          </button>
          <button
            type="button"
            className="tko-btn tko-btn--primary tko-btn--sm"
            disabled={saving || (!officialMode && !shopId)}
            onClick={() => void submit()}
          >
            {saving ? chartText('tiktokPublish.saving', fixedUi("保存中…")) : chartText('shop.save', fixedUi("保存"))}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
